import { useEffect, useState } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormHelperText,
} from "@mui/material";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";

export default function MultiInputFields({
  cell,
  updatedValue,
  disabled = false,
  isQC = false,
  showRequiredField = false,
}: any) {
  const [value, setValue] = useState<string | null>(null);
  const [multipleValue, setMultipleValue] = useState<string[]>([]);

  useEffect(() => {
    if (cell) {
      if (cell?.multiInputElementSettings?.inputType === "MultiSelect") {
        if (isQC) {
          const values = cell?.answers
            ? cell?.answers?.qc?.answer
              ? cell?.answers?.qc?.answer.split(",")
              : cell?.answers?.inspector?.answer &&
                cell?.answers?.inspector?.answer.split(",")
            : [];
          setMultipleValue(values);
        } else {
          setMultipleValue(
            cell?.answers && cell?.answers?.inspector?.answer
              ? cell?.answers?.inspector?.answer.split(",")
              : []
          );
        }
      } else {
        if (isQC) {
          const value = cell?.answers?.qc?.answer
            ? cell?.answers?.qc?.answer
            : cell?.answers?.inspector?.answer || null;
          setValue(value);
        } else {
          setValue(cell?.answers?.inspector?.answer || null);
        }
      }
    }
  }, [cell]); // eslint-disable-line

  useEffect(() => {
    let prevValue: string | null = null;
    if (cell) {
      prevValue = isQC
        ? cell?.answers?.qc?.answer
        : cell?.answers?.inspector?.answer;
    }
    if (value != null && value !== prevValue) {
      const delayInputTimeoutId = setTimeout(() => {
        updatedValue(value, cell?.id);
      }, 800);
      return () => clearTimeout(delayInputTimeoutId);
    }
  }, [value, cell]); // eslint-disable-line

  const onTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (cell?.multiInputElementSettings?.inputFormat === "TextOnly") {
      const filtered = event.target.value.replace(/[^a-zA-Z\s]/g, "");
      setValue(filtered);
    } else {
      setValue(value);
    }
  };

  const onSingleSelectChange = (event: SelectChangeEvent) => {
    const { value } = event.target;
    setValue(value);
  };

  const onMultiSelectChange = (
    event: SelectChangeEvent<typeof multipleValue>
  ) => {
    const {
      target: { value },
    } = event;
    const finalValue = typeof value === "string" ? value.split(",") : value;
    setMultipleValue(finalValue);
    updatedValue(finalValue.join(","), cell?.id);
  };

  return (
    <>
      {cell?.multiInputElementSettings?.inputType === "OpenEnd" &&
        cell?.multiInputElementSettings?.inputFormat === "OpenText" && (
          <TextField
            fullWidth
            multiline
            label={cell?.title}
            value={value || ""}
            onChange={onTextFieldChange}
            disabled={disabled}
            error={showRequiredField && cell?.responseRequired && !value}
            helperText={
              showRequiredField &&
              cell?.responseRequired &&
              !value &&
              "This field is required."
            }
          />
        )}

      {cell?.multiInputElementSettings?.inputType === "OpenEnd" &&
        cell?.multiInputElementSettings?.inputFormat === "TextOnly" && (
          <TextField
            fullWidth
            multiline
            label={cell?.title}
            value={value || ""}
            onChange={onTextFieldChange}
            disabled={disabled}
            error={showRequiredField && cell?.responseRequired && !value}
            helperText={
              showRequiredField &&
              cell?.responseRequired &&
              !value &&
              "This field is required."
            }
          />
        )}

      {cell?.multiInputElementSettings?.inputType === "OpenEnd" &&
        cell?.multiInputElementSettings?.inputFormat === "NumbersOnly" && (
          <TextField
            type="number"
            fullWidth
            label={cell?.title}
            value={value || ""}
            onChange={onTextFieldChange}
            disabled={disabled}
            onKeyDown={(e) => {
              if (
                e.key === "-" ||
                e.key === "e" ||
                e.key === "E" ||
                e.key === "+"
              ) {
                e.preventDefault();
              }
            }}
            error={showRequiredField && cell?.responseRequired && !value}
            helperText={
              showRequiredField &&
              cell?.responseRequired &&
              !value &&
              "This field is required."
            }
          />
        )}

      {cell?.multiInputElementSettings?.inputType === "OpenEnd" &&
        cell?.multiInputElementSettings?.inputFormat === "Currency" && (
          <FormControl fullWidth>
            <InputLabel htmlFor="outlined-adornment-amount">
              {cell?.title}
            </InputLabel>
            <OutlinedInput
              id="outlined-adornment-amount"
              sx={{
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                  {
                    display: "none",
                    margin: 0,
                  },
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
              }}
              type="number"
              startAdornment={
                <InputAdornment position="start">
                  <AttachMoneyOutlinedIcon />
                </InputAdornment>
              }
              label={cell?.title}
              value={value || ""}
              onChange={onTextFieldChange}
              disabled={disabled}
              error={showRequiredField && cell?.responseRequired && !value}
            />
            {showRequiredField && cell?.responseRequired && !value && (
              <FormHelperText sx={{ color: "#d32f2f" }}>
                This field is required.
              </FormHelperText>
            )}
          </FormControl>
        )}

      {cell?.multiInputElementSettings?.inputType === "OpenEnd" &&
        cell?.multiInputElementSettings?.inputFormat === "Percentage" && (
          <FormControl fullWidth>
            <InputLabel htmlFor="outlined-adornment-amount">
              {cell?.title}
            </InputLabel>
            <OutlinedInput
              id="outlined-adornment-amount"
              sx={{
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                  {
                    display: "none",
                    margin: 0,
                  },
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
              }}
              type="number"
              endAdornment={
                <InputAdornment position="end">
                  <PercentOutlinedIcon />
                </InputAdornment>
              }
              label={cell?.title}
              value={value || ""}
              onChange={onTextFieldChange}
              disabled={disabled}
              error={showRequiredField && cell?.responseRequired && !value}
            />
            {showRequiredField && cell?.responseRequired && !value && (
              <FormHelperText sx={{ color: "#d32f2f" }}>
                This field is required.
              </FormHelperText>
            )}
          </FormControl>
        )}

      {cell?.multiInputElementSettings?.inputType === "SingleSelect" && (
        <FormControl
          fullWidth
          error={showRequiredField && cell?.responseRequired && !value}
        >
          <InputLabel id="demo-simple-select-label">{cell?.title}</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            label={cell?.title}
            value={value || ""}
            onChange={onSingleSelectChange}
            disabled={disabled}
          >
            {cell?.answerOptions.map((option: string) => (
              <MenuItem value={option}>{option}</MenuItem>
            ))}
          </Select>
          {showRequiredField && cell?.responseRequired && !value && (
            <FormHelperText sx={{ color: "#d32f2f" }}>
              This field is required.
            </FormHelperText>
          )}
        </FormControl>
      )}

      {cell?.multiInputElementSettings?.inputType === "MultiSelect" && (
        <FormControl
          fullWidth
          error={showRequiredField && cell?.responseRequired && !value}
        >
          <InputLabel id="demo-simple-select-label">{cell?.title}</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            label={cell?.title}
            multiple
            value={multipleValue || ""}
            onChange={onMultiSelectChange}
            disabled={disabled}
          >
            {cell?.answerOptions.map((option: string) => (
              <MenuItem value={option}>{option}</MenuItem>
            ))}
          </Select>
          {showRequiredField && cell?.responseRequired && !value && (
            <FormHelperText sx={{ color: "#d32f2f" }}>
              This field is required.
            </FormHelperText>
          )}
        </FormControl>
      )}
    </>
  );
}
